// ApproachScrollSection.jsx — Premium Dark Animated Redesign
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Search,
  Lightbulb,
  Compass,
  Code2,
  FlaskConical,
  Rocket
} from "lucide-react";

const STEPS = [
  { title: "Discovery",   detailedTitle: "Discovery & Research",   icon: <Search size={28} />,      badge: "Phase 01", subtitle: "REQUIREMENTS GATHERING", desc: "We dive deep into your business goals, target audience, and technical requirements to build a clear project roadmap.",                                                                         color: "#1e3a8a" },
  { title: "Strategy",   detailedTitle: "Strategy & Planning",     icon: <Lightbulb size={28} />,   badge: "Phase 02", subtitle: "ARCHITECTURE BLUEPRINT",  desc: "Our team crafts a comprehensive technical blueprint covering system architecture, data flows, and technology stack selection.",                                                    color: "#0ea5e9" },
  { title: "Design",     detailedTitle: "Design & Architecture",   icon: <Compass size={28} />,     badge: "Phase 03", subtitle: "UI/UX WIREFRAMES",          desc: "We create intuitive interfaces and architectures optimized for performance, usability, and visual excellence.",                                                                    color: "#7c3aed" },
  { title: "Development",detailedTitle: "Development & Build",    icon: <Code2 size={28} />,       badge: "Phase 04", subtitle: "AGILE SPRINTS",              desc: "Using agile methodology, we build feature-rich, scalable applications with clean, maintainable codebases.",                                                                    color: "#f97316" },
  { title: "Testing",    detailedTitle: "Testing & QA",           icon: <FlaskConical size={28} />, badge: "Phase 05", subtitle: "QUALITY ASSURANCE",         desc: "Rigorous automated and manual testing across all devices ensures a bug-free, high-performance product.",                                                                         color: "#16a34a" },
  { title: "Launch",     detailedTitle: "Launch & Scale",         icon: <Rocket size={28} />,      badge: "Phase 06", subtitle: "DEPLOYMENT & GROWTH",       desc: "We deploy to production with CI/CD pipelines, monitor performance, and provide ongoing support for continuous growth.",                                                       color: "#dc2626" },
];

const ApproachScrollSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePrev = () => {
    if (activeStep > 0) { setDirection(-1); setActiveStep(p => p - 1); }
  };
  const handleNext = () => {
    if (activeStep < STEPS.length - 1) { setDirection(1); setActiveStep(p => p + 1); }
  };

  const step = STEPS[activeStep];
  const accent = step.color;

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
  };
  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } }
  };
  const slideCard = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0, scale: 0.94, filter: "blur(6px)" }),
    center: {
      x: 0, opacity: 1, scale: 1, filter: "blur(0px)",
      transition: { x: { type: "spring", stiffness: 260, damping: 24 }, opacity: { duration: 0.35 }, scale: { duration: 0.35 }, filter: { duration: 0.3 } }
    },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0, scale: 0.94, filter: "blur(6px)", transition: { duration: 0.25 } })
  };

  return (
    <div ref={sectionRef} className="appr-root">

      {/* ── Animated Background ── */}
      <div className="appr-bg">
        <motion.div className="appr-orb appr-orb-1"
          animate={{ x: [0, 70, 0], y: [0, -50, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          style={{ background: `radial-gradient(circle, ${accent}28 0%, transparent 70%)` }}
        />
        <motion.div className="appr-orb appr-orb-2"
          animate={{ x: [0, -60, 0], y: [0, 60, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div className="appr-orb appr-orb-3"
          animate={{ x: [0, 40, -30, 0], y: [0, -40, 30, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="appr-dot-grid" />
        {[...Array(16)].map((_, i) => (
          <motion.div key={i} className="appr-particle"
            style={{ left: `${6 + (i * 6) % 88}%`, width: `${2 + i % 3}px`, height: `${2 + i % 3}px`, background: i % 2 === 0 ? accent : "#7c3aed" }}
            animate={{ y: [0, -(70 + i * 7), 0], opacity: [0, 0.45, 0] }}
            transition={{ duration: 5 + i % 5, repeat: Infinity, delay: i * 0.35, ease: "easeInOut" }}
          />
        ))}
      </div>

      <div className="container appr-container">
        <motion.div variants={stagger} initial="hidden" animate={isInView ? "visible" : "hidden"}>

          {/* ── Header ── */}
          <div className="text-center mb-5">
            <motion.div variants={fadeUp} className="appr-label-row">
              <span className="appr-label-line" />
              <span className="appr-label-text">OUR APPROACH</span>
              <span className="appr-label-line" />
            </motion.div>
            <motion.h2 variants={fadeUp} className="appr-main-title">
              How We{" "}
              <motion.span
                className="appr-title-accent"
                key={activeStep}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{ background: `linear-gradient(135deg, ${accent} 0%, #7c3aed 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
              >
                Deliver Excellence
              </motion.span>
            </motion.h2>
            <motion.p variants={fadeUp} className="appr-subtitle mx-auto">
              Our proven methodology ensures your project is completed on time, within budget, and exceeds expectations.
            </motion.p>
          </div>

          {/* ── Timeline ── */}
          <motion.div variants={fadeUp} className="appr-timeline-wrap">
            <div className="appr-track-bg" />
            <motion.div
              className="appr-track-fill"
              animate={{ width: `calc(${(activeStep / (STEPS.length - 1)) * 100}% - 40px)` }}
              transition={{ duration: 0.6, ease: [0.25, 0.8, 0.25, 1] }}
              style={{ background: `linear-gradient(90deg, ${accent}, #7c3aed)` }}
            />
            <div className="appr-nodes-row">
              {STEPS.map((s, i) => {
                const isActive = i === activeStep;
                const isPast = i < activeStep;
                return (
                  <div key={i} className="appr-node-wrap"
                    onClick={() => { setDirection(i > activeStep ? 1 : -1); setActiveStep(i); }}
                    onMouseEnter={() => setHoveredStep(i)}
                    onMouseLeave={() => setHoveredStep(null)}
                  >
                    <motion.div
                      className="appr-node"
                      animate={{
                        background: isActive
                          ? `radial-gradient(circle at 35% 35%, ${s.color} 0%, #0e1e36 100%)`
                          : isPast
                            ? `${s.color}18`
                            : "rgba(255,255,255,0.9)",
                        borderColor: isActive ? s.color : isPast ? `${s.color}70` : "rgba(30,58,138,0.15)",
                        boxShadow: isActive
                          ? `0 0 20px ${s.color}55, 0 0 40px ${s.color}22`
                          : hoveredStep === i
                            ? `0 0 14px ${s.color}40, 0 4px 12px rgba(30,58,138,0.12)`
                            : "0 2px 8px rgba(30,58,138,0.06)",
                        scale: isActive ? 1.15 : hoveredStep === i ? 1.08 : 1,
                        borderRadius: isActive ? "14px" : "50%",
                      }}
                      transition={{ duration: 0.4 }}
                      style={{ color: isActive ? "#fff" : s.color }}
                    >
                      <div style={{ transform: isMobile ? "scale(0.75)" : "none" }}>{s.icon}</div>
                    </motion.div>
                    {isActive && (
                      <motion.div className="appr-pulse-ring"
                        animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        style={{ borderColor: s.color }}
                      />
                    )}
                    <span className="appr-node-label" style={{ color: isActive ? s.color : "#64748b", fontWeight: isActive ? 700 : 500 }}>
                      {isMobile ? `S${i + 1}` : `Step ${i + 1}`}
                    </span>
                    <span className="appr-node-title" style={{ color: isActive ? s.color : "#94a3b8" }}>
                      {s.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* ── Bottom Split Panel ── */}
          <div className="row justify-content-center align-items-center mt-5 appr-panel-row">
            {/* Left Card */}
            <div className="col-12 col-md-5 d-flex justify-content-center mb-4 mb-md-0">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={activeStep}
                  custom={direction}
                  variants={slideCard}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="appr-step-card"
                  style={{ borderColor: `${accent}35` }}
                >
                  <motion.div className="appr-card-glow"
                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                    style={{ background: `radial-gradient(ellipse at 50% 0%, ${accent}30 0%, transparent 60%)` }}
                  />
                  <div className="appr-card-blob" style={{ background: `${accent}18` }} />
                  <div className="appr-card-inner">
                    <motion.div className="appr-card-icon"
                      style={{ background: `radial-gradient(circle at 30% 30%, ${accent} 0%, #0e1e36 100%)`, boxShadow: `0 10px 25px ${accent}40` }}
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {step.icon}
                    </motion.div>
                    <div>
                      <span className="appr-card-badge" style={{ color: accent, background: `${accent}15`, border: `1px solid ${accent}30` }}>
                        {step.badge}
                      </span>
                      <motion.h2 className="appr-step-number"
                        key={activeStep + "n"}
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{ color: accent }}
                      >
                        Step 0{activeStep + 1}
                      </motion.h2>
                      <span className="appr-step-subtitle">{step.subtitle}</span>
                    </div>
                  </div>
                  <div className="appr-card-progress-wrap">
                    <motion.div className="appr-card-progress-fill"
                      key={activeStep}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      style={{ background: accent }}
                    />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right Details */}
            <div className="col-12 col-md-7 ps-md-5 text-center text-md-start">
              <AnimatePresence mode="wait">
                <motion.div key={activeStep} variants={stagger} initial="hidden" animate="visible" exit="hidden">
                  <motion.div variants={fadeUp} className="d-flex align-items-center justify-content-center justify-content-md-start gap-3 mb-3">
                    <motion.span className="appr-detail-line"
                      initial={{ width: 0 }}
                      animate={{ width: "35px" }}
                      transition={{ duration: 0.5 }}
                      style={{ background: accent, display: "block", height: "2px", borderRadius: "2px", flexShrink: 0 }}
                    />
                    <h3 className="appr-detail-title" style={{ color: accent }}>{step.detailedTitle}</h3>
                  </motion.div>
                  <motion.p variants={fadeUp} className="appr-detail-desc">{step.desc}</motion.p>
                  <motion.div variants={fadeUp} className="d-flex align-items-center justify-content-center justify-content-md-start gap-3">
                    <button className="appr-nav-btn" onClick={handlePrev} disabled={activeStep === 0}
                      style={activeStep === 0 ? { opacity: 0.3, cursor: "not-allowed" } : {}}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>
                    <button className="appr-nav-btn" onClick={handleNext} disabled={activeStep === STEPS.length - 1}
                      style={activeStep === STEPS.length - 1 ? { opacity: 0.3, cursor: "not-allowed" } : { background: accent, borderColor: accent, color: "#fff" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                    <span className="appr-fraction">
                      <span style={{ color: accent, fontSize: "1.4rem" }}>0{activeStep + 1}</span>
                      <span style={{ color: "#94a3b8" }}> / 0{STEPS.length}</span>
                    </span>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

        </motion.div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=Manrope:wght@300;400;500;600;700;800&display=swap');

        .appr-root {
          position: relative; overflow: hidden;
          background: linear-gradient(150deg, #faf8f5 0%, #f5f0e8 50%, #faf8f5 100%);
          padding: 6rem 0;
        }
        .appr-bg { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
        .appr-orb { position: absolute; border-radius: 50%; filter: blur(90px); pointer-events: none; }
        .appr-orb-1 { width: 520px; height: 520px; top: -160px; right: -100px; background: radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%); }
        .appr-orb-2 { width: 420px; height: 420px; bottom: -120px; left: -80px; background: radial-gradient(circle, rgba(30,58,138,0.08) 0%, transparent 70%); }
        .appr-orb-3 { width: 300px; height: 300px; top: 40%; left: 35%; background: radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%); }
        .appr-dot-grid { position: absolute; inset: 0; background-image: radial-gradient(rgba(30,58,138,0.07) 1px, transparent 1px); background-size: 30px 30px; }
        .appr-particle { position: absolute; bottom: -8px; border-radius: 50%; opacity: 0.25; }

        .appr-container { position: relative; z-index: 2; }

        .appr-label-row { display: flex; align-items: center; justify-content: center; gap: 0.9rem; margin-bottom: 1rem; }
        .appr-label-line { display: block; width: 32px; height: 1.5px; background: linear-gradient(90deg, transparent, rgba(30,58,138,0.4), transparent); border-radius: 2px; }
        .appr-label-text { font-family: 'Manrope', sans-serif; font-size: 0.72rem; font-weight: 800; letter-spacing: 3.5px; color: rgba(30,58,138,0.55); text-transform: uppercase; }

        .appr-main-title { font-family: 'Space Grotesk', sans-serif; font-size: clamp(2.2rem, 5vw, 3.6rem); font-weight: 700; color: #0f172a; line-height: 1.15; letter-spacing: -1.2px; margin-bottom: 1rem; }
        .appr-title-accent { font-family: 'Playfair Display', serif; font-style: italic; font-weight: 700; display: inline-block; letter-spacing: -0.5px; }
        .appr-subtitle { font-family: 'Manrope', sans-serif; font-size: 1.1rem; color: #64748b; line-height: 1.8; max-width: 600px; font-weight: 400; }

        .appr-timeline-wrap { position: relative; max-width: 1000px; margin: 0 auto; padding: 0 20px 70px; }
        .appr-track-bg { position: absolute; top: 34px; left: 50px; right: 50px; height: 3px; background: rgba(30,58,138,0.1); border-radius: 2px; z-index: 0; }
        .appr-track-fill { position: absolute; top: 34px; left: 50px; height: 3px; border-radius: 2px; z-index: 1; }
        .appr-nodes-row { display: flex; justify-content: space-between; align-items: flex-start; position: relative; z-index: 2; }
        .appr-node-wrap { display: flex; flex-direction: column; align-items: center; cursor: pointer; position: relative; width: 110px; }
        .appr-node { width: 68px; height: 68px; display: flex; align-items: center; justify-content: center; border: 1.5px solid rgba(30,58,138,0.15); background: rgba(255,255,255,0.7); backdrop-filter: blur(8px); cursor: pointer; box-shadow: 0 2px 8px rgba(30,58,138,0.08); }
        .appr-pulse-ring { position: absolute; top: -11px; left: -11px; width: 90px; height: 90px; border-radius: 50%; border: 1.5px solid; z-index: 1; pointer-events: none; }
        .appr-node-label { font-family: 'Manrope', sans-serif; font-size: 0.75rem; font-weight: 600; margin-top: 12px; letter-spacing: 0.3px; transition: color 0.3s ease; color: #64748b; }
        .appr-node-title { font-family: 'Manrope', sans-serif; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; margin-top: 3px; transition: color 0.3s ease; text-align: center; color: #94a3b8; }

        .appr-step-card { width: 100%; max-width: 430px; min-height: 260px; background: rgba(255,255,255,0.75); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid rgba(30,58,138,0.1); border-radius: 28px; padding: 2.5rem; position: relative; overflow: hidden; box-shadow: 0 20px 50px rgba(30,58,138,0.1), 0 4px 15px rgba(30,58,138,0.06), inset 0 1px 0 rgba(255,255,255,0.9); }
        .appr-card-glow { position: absolute; top: 0; left: 0; right: 0; height: 200px; pointer-events: none; z-index: 0; }
        .appr-card-blob { position: absolute; top: -20px; right: -20px; width: 130px; height: 130px; border-radius: 50%; pointer-events: none; }
        .appr-card-inner { position: relative; z-index: 1; display: flex; flex-direction: column; justify-content: space-between; height: 100%; gap: 1.75rem; }
        .appr-card-icon { width: 66px; height: 66px; border-radius: 18px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
        .appr-card-badge { font-family: 'Manrope', sans-serif; font-size: 0.65rem; font-weight: 800; padding: 0.3rem 0.75rem; border-radius: 20px; letter-spacing: 1.2px; text-transform: uppercase; display: inline-block; margin-bottom: 0.5rem; }
        .appr-step-number { font-family: 'Space Grotesk', sans-serif; font-size: 2.8rem; font-weight: 700; letter-spacing: -2px; line-height: 1; margin: 0 0 0.35rem; }
        .appr-step-subtitle { font-family: 'Manrope', sans-serif; font-size: 0.78rem; font-weight: 800; color: #94a3b8; letter-spacing: 1.5px; text-transform: uppercase; }
        .appr-card-progress-wrap { position: absolute; bottom: 0; left: 0; right: 0; height: 4px; background: rgba(30,58,138,0.08); border-radius: 0 0 28px 28px; overflow: hidden; }
        .appr-card-progress-fill { height: 100%; width: 100%; transform-origin: left; border-radius: inherit; }

        .appr-detail-title { font-family: 'Space Grotesk', sans-serif; font-size: 2rem; font-weight: 700; margin: 0; letter-spacing: -0.5px; }
        .appr-detail-desc { font-family: 'Playfair Display', serif; font-size: 1.2rem; line-height: 1.8; color: #475569; font-weight: 400; font-style: italic; margin-bottom: 2.25rem; }

        .appr-nav-btn { width: 56px; height: 56px; border-radius: 50%; border: 1.5px solid rgba(30,58,138,0.2); background: rgba(255,255,255,0.8); color: #334155; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s ease; backdrop-filter: blur(8px); box-shadow: 0 2px 8px rgba(30,58,138,0.08); }
        .appr-nav-btn:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.3); }
        .appr-fraction { font-family: 'Space Grotesk', sans-serif; font-size: 1.1rem; font-weight: 700; letter-spacing: 1px; color: #94a3b8; }
        .appr-panel-row { max-width: 1100px; margin: 0 auto; }

        @media (max-width: 768px) {
          .appr-root { padding: 3.5rem 0; }
          .appr-node { width: 46px; height: 46px; }
          .appr-node-wrap { width: 58px; }
          .appr-pulse-ring { width: 64px; height: 64px; top: -9px; left: -9px; }
          .appr-step-card { max-width: 100%; }
          .appr-detail-title { font-size: 1.5rem; }
          .appr-main-title { font-size: 2.1rem; }
        }
      `}</style>
    </div>
  );
};

export default ApproachScrollSection;
